import { useMemo } from 'react';
import { formatCurrency } from '../lib/utils';

export interface PerDiemResult {
  perDiem: number;
  rawPerDiem: number;
  previousPerDiem: number;
  rawPreviousPerDiem: number;
  daysRemaining: number;
  monthlyIncome: number;
  monthlyFixedExpenses: number;
  monthlyDiscretionarySpend: number;
  dailyDiscretionaryBudget: number;
  monthlyDiscretionaryBudget: number;
  isOverBudget: boolean;
  overBudgetAmount: number;
}

export interface FireImpact {
  dailyDiff: number;
  monthsDiff: number;
  isUnderspending: boolean;
}

interface UseFireImpactProps {
  perDiem: PerDiemResult | null | undefined;
  firePlan: { beginYear?: number | null } | null | undefined;
  accounts: Array<{ type: string; balance: string | number | null }> | null | undefined;
}

export function useFireImpact({ perDiem, firePlan, accounts }: UseFireImpactProps) {
  return useMemo(() => {
    if (!perDiem || !firePlan?.beginYear || !accounts) return null;

    // Daily difference: positive = underspending (saving extra), negative = overspending
    const dailyDiff = perDiem.rawPerDiem - perDiem.dailyDiscretionaryBudget;
    // Ignore negligible differences (less than $1/day)
    if (Math.abs(dailyDiff) < 1) return null;

    const annualExtraSavings = dailyDiff * 365;

    // Baseline annual savings from budget: income - fixed - discretionary
    const baselineMonthlyBudgetedExpenses =
      perDiem.monthlyFixedExpenses +
      perDiem.dailyDiscretionaryBudget * new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    const baselineAnnualSavings = (perDiem.monthlyIncome - baselineMonthlyBudgetedExpenses) * 12;

    // FI number = 25x annual expenses (4% SWR)
    const annualExpenses = baselineMonthlyBudgetedExpenses * 12;
    const fiNumber = annualExpenses * 25;

    // Current investable assets
    const investableAssets = accounts
      .filter((a) => a.type === 'investment' || a.type === 'depository')
      .reduce((sum, a) => sum + Number(a.balance ?? 0), 0);

    const REAL_RETURN = 0.07;

    // Calculate years to FI with baseline savings
    const yearsToFI = (annualSavings: number): number | null => {
      if (investableAssets >= fiNumber) return 0;
      if (annualSavings <= 0) return null;
      for (let n = 0; n <= 60; n++) {
        const fv =
          investableAssets * Math.pow(1 + REAL_RETURN, n) +
          annualSavings * ((Math.pow(1 + REAL_RETURN, n) - 1) / REAL_RETURN);
        if (fv >= fiNumber) return n;
      }
      return null;
    };

    const baselineYears = yearsToFI(baselineAnnualSavings);
    const adjustedYears = yearsToFI(baselineAnnualSavings + annualExtraSavings);

    if (baselineYears === null || adjustedYears === null) return null;

    // Difference in months (positive = sooner, negative = later)
    const monthsDiff = Math.round((baselineYears - adjustedYears) * 12);
    if (monthsDiff === 0) return null;

    return {
      dailyDiff: Math.abs(dailyDiff),
      monthsDiff,
      isUnderspending: dailyDiff > 0,
    };
  }, [perDiem, firePlan, accounts]);
}
